import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  Container,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import { RedriveHistory } from "./components/RedriveHistory";
import { useSelectedExecution } from "./hooks/useSelectedExecution";
import { StageSelector } from "./components/StageSelector";
import { FileUploader } from "./components/FileUploader";
import { useExecutePaymentRedrive } from "./hooks/useExecutePaymentRedrive";
import { usePaymentFileValidation } from "./hooks/usePaymentFileValidation";
import { PAYMENT_REDRIVE } from "./redriveKinds";
import { StageContext } from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";

interface Props {
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
}

export default function PaymentRedriveContent({ setStage }: Props) {
  const stage = useContext(StageContext);
  const [loading, setLoading] = useState(false);
  const {
    selectedExecutionId,
    setSelectedExecutionId,
    selectExecution,
    clearSelection,
  } = useSelectedExecution();
  const {
    file,
    error,
    redriveOK,
    handleFileChange,
    validateRedrive,
    setError,
    removeDuplicates,
  } = usePaymentFileValidation();
  const { executeRedrive: executeRedriveHook } = useExecutePaymentRedrive();

  const executeRedrive = async () => {
    if (file) {
      setLoading(true);
      await executeRedriveHook(file, stage, setSelectedExecutionId, setError);
      setLoading(false);
    }
  };

  return (
    <SpaceBetween size="xl">
      <SpaceBetween size="l">
        {error && <ErrorAlert content={error} header="Payment Redrive Error" />}
        {selectedExecutionId && (
          <Button iconName="arrow-left" onClick={clearSelection} variant="link">
            Back to all redrives
          </Button>
        )}

        <Container
          header={<Box variant="h2">Payment Document Redrive Tool</Box>}
        >
          <SpaceBetween size="m">
            <Box variant="p">
              This tool allows you to redrive payment documents. Upload a CSV
              file with payment IDs (one per line), or redrive a single payment
              from that payment&#39;s document view.
            </Box>
            {!selectedExecutionId && (
              <StageSelector stage={stage} setStage={setStage} />
            )}
          </SpaceBetween>
        </Container>

        {!selectedExecutionId && (
          <Container>
            <SpaceBetween size="m">
              <FileUploader
                file={file}
                handleFileChange={handleFileChange}
                validateRedrive={validateRedrive}
                redriveOK={redriveOK}
                removeDuplicates={removeDuplicates}
                loading={loading}
              />
              {file && redriveOK && (
                <Button
                  onClick={executeRedrive}
                  variant="primary"
                  disabled={loading}
                  loading={loading}
                >
                  Execute Redrive
                </Button>
              )}
            </SpaceBetween>
          </Container>
        )}

        <RedriveHistory
          kind={PAYMENT_REDRIVE}
          stage={stage}
          selectedExecutionId={selectedExecutionId}
          onSelectExecution={selectExecution}
        />
      </SpaceBetween>
    </SpaceBetween>
  );
}
